import { create } from "zustand";
import { createJSONStorage, persist, StateStorage } from "zustand/middleware";

// The release letter's state: when each letter (by release slug) was first
// opened. The nav card reads it for its unread seal and how long it stays,
// and the homepage reads it for its follow-up.

type AnnouncementLetterState = {
  // Letter key -> ISO time it was first opened.
  readAt: Record<string, string>;
  markRead: (key: string) => void;
  clearAll: () => void;
};

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

const safeLocalStorage: StateStorage = {
  getItem: (name) => {
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      window.localStorage.setItem(name, value);
    } catch {
      return;
    }
  },
  removeItem: (name) => {
    try {
      window.localStorage.removeItem(name);
    } catch {
      return;
    }
  },
};

const sanitizeReadAt = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== "object") return {};
  return Object.fromEntries(
    Object.entries(value).filter(
      ([, at]) => typeof at === "string" && !Number.isNaN(Date.parse(at)),
    ),
  );
};

export const useAnnouncementLetterStore = create<AnnouncementLetterState>()(
  persist(
    (set) => ({
      readAt: {},
      markRead: (key) =>
        set((state) =>
          state.readAt[key]
            ? state
            : { readAt: { ...state.readAt, [key]: new Date().toISOString() } },
        ),
      clearAll: () => set({ readAt: {} }),
    }),
    {
      name: "announcement-letter",
      version: 1,
      storage: createJSONStorage(() =>
        typeof window === "undefined" ? noopStorage : safeLocalStorage,
      ),
      partialize: (state) => ({ readAt: state.readAt }),
      merge: (persisted, current) => ({
        ...current,
        readAt: sanitizeReadAt(
          (persisted as { readAt?: unknown } | null)?.readAt,
        ),
      }),
    },
  ),
);

export const clearAnnouncementLetterState = (): void => {
  useAnnouncementLetterStore.getState().clearAll();
  useAnnouncementLetterStore.persist.clearStorage();
};
